import { X, Check } from "lucide-react";
import { Reveal } from "@/components/Reveal";

const COLS = [
  {
    title: "INDICATORS",
    sub: "Signals without a complete process",
    rows: [
      "Tell you when without always explaining why",
      "May lack broader position context",
      "May not provide a complete risk framework",
      "Often provide little execution accountability",
    ],
    tone: "dim",
    testid: "comparison-col-indicators",
  },
  {
    title: "COURSES",
    sub: "Knowledge without continuous execution support",
    rows: [
      "Information alone doesn't create discipline",
      "Knowing a setup doesn't guarantee execution",
      "Education can end when the videos end",
      "Little accountability after purchase",
    ],
    tone: "dim",
    testid: "comparison-col-courses",
  },
  {
    title: "THE P.E.E. ECOSYSTEM",
    sub: "Methodology + Technology + Development",
    rows: [
      "Position • Event • Entry Game Plan",
      "Purpose-built P.E.E. OS technology",
      "Defined entry, stop, TP1 and TP2 structure",
      "Trade lifecycle and validation",
      "Alerts as trade status changes",
      "Analytics that measure the process",
      "Executor Training",
      "Coaching, review and accountability",
    ],
    tone: "neon",
    testid: "comparison-col-pee-ecosystem",
  },
];

const CLOSING = ["LEARN IT.", "EXECUTE IT.", "MEASURE IT.", "IMPROVE IT."];

export default function Comparison() {
  return (
    <section data-testid="comparison-section" className="border-t border-line py-24 lg:py-32">
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="text-center">
          <Reveal>
            <div className="mb-5 flex items-center justify-center gap-3 font-mono text-[11px] uppercase tracking-[0.25em] text-neon/90">
              <span className="h-px w-8 bg-neon/50" />
              The Difference
              <span className="h-px w-8 bg-neon/50" />
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="font-display text-3xl font-extrabold uppercase leading-[1.05] tracking-tight text-white sm:text-4xl lg:text-5xl">
              Not another signal.
              <br />
              <span className="glow-text-neon text-neon">A defined process.</span>
            </h2>
          </Reveal>
        </div>

        <div className="mt-16 grid gap-4 lg:grid-cols-3">
          {COLS.map((c, i) => (
            <Reveal key={c.title} delay={0.1 * i} className="h-full">
              <div
                data-testid={c.testid}
                className={`h-full rounded-lg border p-8 ${
                  c.tone === "neon"
                    ? "glow-neon border-neon/60 bg-charcoal"
                    : "border-line bg-charcoal/50"
                }`}
              >
                <div
                  className={`font-display text-xl font-extrabold uppercase tracking-tight ${
                    c.tone === "neon" ? "text-neon" : "text-mist"
                  }`}
                >
                  {c.title}
                </div>
                <div className="mt-2 font-mono text-[10px] uppercase tracking-[0.2em] text-dim">
                  {c.sub}
                </div>
                <ul className="mt-7 space-y-4">
                  {c.rows.map((r) => (
                    <li key={r} className="flex items-start gap-3 text-sm">
                      {c.tone === "neon" ? (
                        <Check size={15} className="mt-0.5 shrink-0 text-neon" />
                      ) : (
                        <X size={15} className="mt-0.5 shrink-0 text-risk/70" />
                      )}
                      <span className={c.tone === "neon" ? "text-white" : "text-mist"}>
                        {r}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-16 flex flex-wrap items-center justify-center gap-x-8 gap-y-3">
          {CLOSING.map((l, i) => (
            <Reveal key={l} delay={0.08 * i}>
              <span
                className={`font-display text-xl font-extrabold uppercase tracking-tight sm:text-3xl ${
                  i === CLOSING.length - 1 ? "glow-text-neon text-neon" : "text-white"
                }`}
              >
                {l}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
}
